import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TableComponent } from '../../../../shared/components/table/table.component';
import { StatusLabelComponent } from '../../../../shared/components/status-label/status-label.component';
import { Certificate } from '../../../../shared/models/certificate.models';
import { Status } from '../../../../shared/models/status-label.model';
import { CertificateService } from '../../../../core/services/certificate.service';
import { MatDialog } from '@angular/material/dialog';
import { InstallSslCertificateDialogComponent } from '../../dialogs/install-ssl-certificate-dialog/install-ssl-certificate-dialog.component';
import { catchError, filter, map, of, switchMap, take } from 'rxjs';
import { OriginVhostService } from '../../services/origin-vhost.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { CertificateType } from '../../../../shared/models/certificate-type.enum';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StatusModalService } from '../../../../core/services/status-modal.service';
import { SnackbarComponent } from '../../../../shared/components/snackbar/snackbar.component';
import { ImportSslCertificateDialogComponent } from '../../dialogs/import-ssl-certificate-dialog/import-ssl-certificate-dialog.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmationDialogComponent } from '../../../../shared/dialogs/confirmation-dialog/confirmation-dialog.component';
import { SslCertificateInfoDialogComponent } from '../../dialogs/ssl-certificate-info-dialog/ssl-certificate-info-dialog.component';
import moment from 'moment';
import { WebsiteExtended } from '../../../../shared/models/website-overview.model';

@Component({
  selector: 'app-ssl-certificates',
  imports: [
    FontAwesomeModule,
    MatButtonModule,
    MatCardModule,
    TableComponent,
    MatTableModule,
    StatusLabelComponent,
    MatTooltipModule,
  ],
  templateUrl: './ssl-certificates.component.html',
  styleUrl: './ssl-certificates.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SslCertificatesComponent {
  private certificateService = inject(CertificateService);

  private originVhostService = inject(OriginVhostService);

  private dialog = inject(MatDialog);

  private snackbar = inject(MatSnackBar);

  private statusModalService = inject(StatusModalService);

  website = input.required<WebsiteExtended>();

  certificates = input.required<Certificate[]>();

  websiteAliases$ = toObservable(this.website).pipe(
    switchMap((website) => this.originVhostService.getVhostByWebsiteId(website.id)),
    map((vhost) => vhost.aliases),
    catchError(() => of([])),
  );

  certificatesColumns = [
    {
      columnDef: 'type',
      header: 'Type',
      cell: (element: Certificate) => `${element.type}`,
    },
    {
      columnDef: 'aliases',
      header: 'Aliases',
      cell: (element: Certificate) => `${element.altNames}`,
    },
    {
      columnDef: 'status',
      header: 'Status',
      cell: (element: Certificate) => `${element.status}`,
      useTemplate: true,
    },
    {
      columnDef: 'issuedAt',
      header: 'Issued At',
      cell: (el: Certificate) =>
        `${el.issuedAt ? moment.utc(el.issuedAt, 'MMM D HH:mm:ss YYYY z').format('DD.MM.YYYY, hh:mm:ss') : '-'}`,
    },
    {
      columnDef: 'expiresAt',
      header: 'Expires At',
      cell: (el: Certificate) =>
        `${el.expiresAt ? moment.utc(el.expiresAt, 'MMM D HH:mm:ss YYYY z').format('DD.MM.YYYY, hh:mm:ss') : '-'}`,
    },
    {
      columnDef: 'buttons',
      header: '',
      cell: () => '',
      useTemplate: true,
    },
  ];

  toStatus(key: string): Status {
    return key.toUpperCase() in Status
      ? Status[key.toUpperCase() as keyof typeof Status]
      : Status.ERROR;
  }

  openInstallSSLDialog(): void {
    this.websiteAliases$
      .pipe(
        switchMap((aliases) =>
          this.dialog
            .open(InstallSslCertificateDialogComponent, {
              data: { subTitle: this.website().domain, websiteId: this.website().id, aliases },
            })
            .afterClosed(),
        ),
        take(1),
        filter((x) => !!x),
        switchMap((data) =>
          data.type === CertificateType.SelfSigned
            ? this.certificateService.createSelfSignedCertificate(data)
            : this.certificateService.createLetsEncryptCertificate(data),
        ),
      )
      .subscribe({
        next: () =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `SSL certificated installation started`,
            },
          }),
        error: (error) =>
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          }),
      });
  }

  openImportSSLDialog(): void {
    this.dialog
      .open(ImportSslCertificateDialogComponent, {
        data: { subTitle: this.website().domain, websiteId: this.website().id },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => !!x),
        switchMap((data) => this.certificateService.registerCustomCertificate(data)),
      )
      .subscribe({
        next: () =>
          this.snackbar.openFromComponent(SnackbarComponent, {
            data: {
              text: `SSL certificated installation started`,
            },
          }),
        error: (error) =>
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          }),
      });
  }

  renewCertificate(id: number): void {
    this.certificateService.renewCertificate(id).subscribe({
      next: () =>
        this.snackbar.openFromComponent(SnackbarComponent, {
          data: {
            text: `Certificate renewal started`,
          },
        }),
      error: (error) =>
        this.statusModalService.open({
          state: 'error',
          message: error.error?.error,
        }),
    });
  }

  openCertificateInfoDialog(certificate: Certificate): void {
    this.dialog.open(SslCertificateInfoDialogComponent, {
      data: {
        subTitle: this.website().domain,
        certificate,
      },
    });
  }

  deleteCertificate(certificate: Certificate): void {
    console.log(certificate.id);
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete Certificate',
          primaryButtonText: 'Delete SSL Certificate',
          type: 'delete',
          paragraphs: [
            `Are you sure you want to delete the SSL Certificate for ${certificate.domain}?`,
          ],
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter((x) => x),
        switchMap(() => this.certificateService.deleteCertificate(certificate.id)),
      )
      .subscribe({
        next: () =>
          this.statusModalService.open({
            state: 'success',
            message: 'SSL Certificate deleted successfully',
          }),
        error: (error) =>
          this.statusModalService.open({
            state: 'error',
            message: error.error?.error,
          }),
      });
  }
}
